import React from 'react';
import { useHistory } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import { VimeoVideo } from 'components/Video';
import { useSignup } from '../context/SignupContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  width: 100%;
`;

const WelcomeCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 32px;

  h1 {
    font-size: 32px;
    font-weight: 700;
    color: #ffffff;
    margin: 0 0 8px 0;

    @media (max-width: 768px) {
      font-size: 28px;
    }
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #94a3b8;
  text-align: center;
  margin: 0 0 32px 0;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 32px;
  margin-bottom: 32px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const VideoContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  align-self: center;

  @media (max-width: 968px) {
    order: 1;
  }
`;

const BenefitsSidebar = styled.div`
  background: rgba(74, 222, 128, 0.08);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 12px;
  padding: 24px;
  height: fit-content;

  @media (max-width: 968px) {
    order: 2;
  }
`;

const BenefitsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #4ade80;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: start;
  gap: 12px;
`;

const BenefitIcon = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: rgba(74, 222, 128, 0.15);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4ade80;
  margin-top: 2px;

  svg {
    width: 18px;
    height: 18px;
    stroke: #4ade80;
  }
`;

const BenefitText = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
`;

const BenefitDescription = styled.div`
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  button {
    min-width: 240px;
  }
`;

const WelcomeStep = () => {
  const history = useHistory();
  const { formData } = useSignup();

  // Get platform name based on environment variable
  const getPlatformName = () => {
    const platform = process.env.REACT_APP_PLATFORM;
    switch (platform) {
      case 'pitpay':
        return 'Pit Pay';
      case 'tickethoss':
        return 'Ticket Hoss';
      case 'kartpass':
        return 'Kart Pass';
      default:
        return 'Pit Pay';
    }
  };

  // Get video ID based on platform
  const getVideoId = () => {
    const platform = process.env.REACT_APP_PLATFORM;
    return platform === 'tickethoss' ? '1156162515' : '1156162446';
  };

  const platformName = getPlatformName();
  const videoId = getVideoId();

  const handleContinue = () => {
    // If user is existing user, skip user info and go to business details
    if (formData.is_existing_user) {
      history.push('/promoter-signup/business-details');
    } else {
      history.push('/promoter-signup/user-info');
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <WelcomeCard>
            <Logo>
              <h1>Welcome to {platformName}</h1>
            </Logo>

            <Subtitle>
              Let's get your racing business set up for digital ticketing. We'll
              guide you through a simple process to get started.
            </Subtitle>

            <TwoColumnLayout>
              <BenefitsSidebar>
                <BenefitsTitle>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  What You Get
                </BenefitsTitle>
                <BenefitsList>
                  <BenefitItem>
                    <BenefitIcon>
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </BenefitIcon>
                    <BenefitText>
                      <BenefitTitle>No Subscription Fees</BenefitTitle>
                      <BenefitDescription>
                        Only pay per transaction - no monthly costs
                      </BenefitDescription>
                    </BenefitText>
                  </BenefitItem>

                  <BenefitItem>
                    <BenefitIcon>
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                        />
                      </svg>
                    </BenefitIcon>
                    <BenefitText>
                      <BenefitTitle>Built for Racing</BenefitTitle>
                      <BenefitDescription>
                        Digital ticketing designed specifically for racing
                        events
                      </BenefitDescription>
                    </BenefitText>
                  </BenefitItem>

                  <BenefitItem>
                    <BenefitIcon>
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
                        />
                      </svg>
                    </BenefitIcon>
                    <BenefitText>
                      <BenefitTitle>Weather Protection</BenefitTitle>
                      <BenefitDescription>
                        Flexible refund policies for weather delays
                      </BenefitDescription>
                    </BenefitText>
                  </BenefitItem>

                  <BenefitItem>
                    <BenefitIcon>
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    </BenefitIcon>
                    <BenefitText>
                      <BenefitTitle>Real-time Analytics</BenefitTitle>
                      <BenefitDescription>
                        Track sales and attendance in real time
                      </BenefitDescription>
                    </BenefitText>
                  </BenefitItem>
                </BenefitsList>
              </BenefitsSidebar>

              <VideoContainer>
                <VimeoVideo vimeoId={videoId} autoplay muted loop />
              </VideoContainer>
            </TwoColumnLayout>

            <ButtonContainer>
              <ButtonV2 onClick={handleContinue} primary>
                Let's Get Started →
              </ButtonV2>
            </ButtonContainer>
          </WelcomeCard>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default WelcomeStep;
